<template>
  <ui-modal-layout :title="tr('Image content settings')" :icon="ImagePlusIcon">
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-foreground">{{ tr('Text on image') }}</span>
      <enhanced-textarea
        v-model="insideEntity.visual_text"
        :placeholder="tr('Enter text to display on the image')"
      />
    </div>

    <div class="grid grid-cols-[2fr_1fr] gap-3 max-sm:grid-cols-1">
      <div class="flex min-w-0 flex-col gap-1.5">
        <span class="text-sm font-medium text-foreground">{{ tr('Font family') }}</span>
        <ui-combobox-select
          v-model="insideEntity.visual_font_family"
          :options="fontFamilyOptions"
          :search-placeholder="tr('Search')"
          :empty-text="tr('No results')"
        >
          <template #option="{ label }">
            <span :style="{ fontFamily: label, fontWeight: insideEntity.visual_font_weight }">{{ label }}</span>
          </template>
        </ui-combobox-select>
      </div>

      <div class="flex min-w-0 flex-col gap-1.5">
        <span class="text-sm font-medium text-foreground">{{ tr('Font weight') }}</span>
        <ui-select
          :model-value="String(insideEntity.visual_font_weight)"
          @update:model-value="(value) => insideEntity.visual_font_weight = fontWeightOptions.find(weight => String(weight) === value) ?? value"
        >
          <ui-select-trigger class="w-full" :aria-label="tr('Font weight')">
            <ui-select-value />
          </ui-select-trigger>
          <ui-select-content>
            <ui-select-item v-for="weight in fontWeightOptions" :key="weight" :value="String(weight)">
              <span :style="{ fontFamily: insideEntity.visual_font_family, fontWeight: weight }">{{ getFontWeightLabel(weight) }}</span>
            </ui-select-item>
          </ui-select-content>
        </ui-select>
      </div>
    </div>

    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-foreground">{{ tr('Logo') }}</span>
      <ui-button
        v-if="insideEntity.logo && !newLogo"
        variant="destructive"
        class="w-full"
        @click="() => insideEntity.logo = null"
      >
        <trash-2-icon />
        {{ tr('Delete uploaded Logo') }}
      </ui-button>
      <image-input
        v-else
        ref="logoInput"
        v-model="newLogo"
        :label="tr('Upload your Logo')"
        :active-label="tr('Delete uploaded Logo')"
        accept="image/jpeg, image/png, image/webp, image/svg+xml"
      />
    </div>

    <div v-if="insideEntity.logo" class="flex flex-col gap-2">
      <span class="text-sm font-medium text-foreground">{{ tr('Logo size') }}</span>
      <ui-slider
        :model-value="[ Number(insideEntity.visual_logo_size) ]"
        :min="50"
        :max="150"
        :aria-label="tr('Logo size')"
        @update:model-value="(value) => insideEntity.visual_logo_size = value[0]"
      />
      <div class="flex justify-between text-xs text-muted-foreground">
        <span>{{ tr('Smaller') }}</span>
        <span>{{ tr('Larger') }}</span>
      </div>
    </div>

    <section class="flex flex-col gap-2">
      <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Preview') }}</h3>
      <magic-tool-generated-image-renderer
        ref="imageRenderer"
        class="overflow-hidden rounded-xl [&_canvas]:w-full"
        :image-url="insideEntity.image.url"
        :aspect-ratio="insideEntity.visual_format"
        :logo-url="insideEntity.logo?.url"
        :logo-size="insideEntity.visual_logo_size"
        :text="insideEntity.visual_text"
        :font-family="insideEntity.visual_font_family"
        :font-weight="insideEntity.visual_font_weight"
        :language="insideEntity.language"
      />
    </section>

    <template #footer>
      <ui-button :disabled="isSaving" @click="() => saveChanges()">
        <loader-circle-icon v-if="isSaving" class="animate-spin" />
        {{ tr('Save settings') }}
      </ui-button>
    </template>
  </ui-modal-layout>
</template>

<script>
import { ImagePlus, LoaderCircle, Trash2 } from '@lucide/vue';
import { markRaw } from 'vue';

import ContentGenerationItem from '@/entities/ContentGenerationItem.js';
import CreatorLiteService from '@/services/CreatorLite.js';
import EnhancedTextarea from '@/ui/components/EnhancedTextarea.vue';
import ImageInput from '@/ui/components/ImageInput.vue';
import MagicToolGeneratedImageRenderer from '@/ui/components/MagicToolGeneratedImageRenderer.vue';

export default {
  name: 'CreatorLiteImageSettings',
  components: {
    EnhancedTextarea,
    ImageInput,
    LoaderCircleIcon: LoaderCircle,
    MagicToolGeneratedImageRenderer,
    Trash2Icon: Trash2,
  },
  props: {
    entity: {
      type: ContentGenerationItem,
      required: true,
    },
  },
  data: () => ({
    ImagePlusIcon: markRaw(ImagePlus),
    isSaving: false,
    insideEntity: new ContentGenerationItem(),
    newLogo: null,
  }),
  computed: {
    fontFamilies() {
      return this.$store.getters['app/fontFamilies'];
    },
    fontFamilyOptions() {
      return this.fontFamilies.map(font => font.name);
    },
    fontWeightOptions() {
      return this.fontFamilies.find(font => font.name === this.insideEntity.visual_font_family)?.variants ?? [];
    },
  },
  watch: {
    fontWeightOptions(newOptions) {
      if (!newOptions.includes(this.insideEntity.visual_font_weight)) {
        this.insideEntity.visual_font_weight = 400; // Regular.
      }
    },
    newLogo(newLogo) {
      if (!newLogo) {
        this.insideEntity.logo = null;
      }
      else {
        this.$nextTick(() => this.insideEntity.logo = { url: this.$refs.logoInput.imagePreview });
      }
    },
  },
  created() {
    this.insideEntity = new ContentGenerationItem(this.entity);
  },
  methods: {
    saveChanges() {
      if (this.isSaving) {
        return;
      }

      this.isSaving = true;

      CreatorLiteService.update(this.insideEntity, { logo: this.newLogo })
        .then(resp => {
          this.entity.updateWith(resp);

          this.$toast.clear();
          this.$toast.success(this.tr('Image content settings saved successfully!'));

          this.$emit('close');
          return resp;
        })
        .catch(err => this.error(err))
        .finally(() => {
          this.isSaving = false;
        });
    },
    getFontWeightLabel(fontWeight) {
      const fontWeightLabels = {
        '100': 'Thin',
        '200': 'Extra Light',
        '300': 'Light',
        '400': 'Regular',
        '500': 'Medium',
        '600': 'Semi Bold',
        '700': 'Bold',
        '800': 'Extra Bold',
        '900': 'Black',
      };
      return fontWeightLabels[fontWeight] || fontWeight;
    },
  },
};
</script>
